"use client";

import { useEffect, useState } from "react";

import { Button, cn } from "@pulseprep/shared-ui";

// ─── Types ────────────────────────────────────────────────────────────────────

interface OtpResendTimerProps {
  /** Total countdown duration in seconds (e.g. 60). */
  durationSeconds: number;
  /**
   * localStorage key used to persist the end timestamp.
   * Unique per flow so multiple OTP flows don't conflict.
   * When the key changes, remount this component (`key={storageKey}`) so the countdown re-initialises.
   */
  storageKey: string;
  /**
   * Called when the user clicks Resend.
   * Timer resets automatically after this is called.
   */
  onResend: () => void;
  className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * OtpResendTimer — shows a live countdown, then a Resend button once expired.
 *
 * Persists the end-timestamp in localStorage so the timer survives page
 * refreshes. On resend the timer always restarts from `durationSeconds`.
 */
export function OtpResendTimer({
  durationSeconds,
  storageKey,
  onResend,
  className,
}: OtpResendTimerProps) {
  // null = not yet hydrated (avoids SSR mismatch)
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  // ── Initialise from localStorage on mount / when storageKey changes ────────
  useEffect(() => {
    setSecondsLeft(null);
    const timeout = setTimeout(() => {
      const stored = localStorage.getItem(storageKey);

      if (stored) {
        const remaining = Math.ceil((parseInt(stored, 10) - Date.now()) / 1000);
        setSecondsLeft(remaining > 0 ? remaining : 0);
        return;
      }

      const endTime = Date.now() + durationSeconds * 1000;
      localStorage.setItem(storageKey, String(endTime));
      setSecondsLeft(durationSeconds);
    }, 0);

    return () => clearTimeout(timeout);
  }, [durationSeconds, storageKey]);

  // ── Tick every second while counting ──────────────────────────────────────
  useEffect(() => {
    if (secondsLeft === null || secondsLeft <= 0) return;

    const interval = setInterval(() => {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return;

      const remaining = Math.ceil((parseInt(stored, 10) - Date.now()) / 1000);
      if (remaining <= 0) {
        setSecondsLeft(0);
        clearInterval(interval);
      } else {
        setSecondsLeft(remaining);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [secondsLeft, storageKey]);

  // ── Resend handler ─────────────────────────────────────────────────────────
  const handleResend = () => {
    onResend();

    const endTime = Date.now() + durationSeconds * 1000;
    localStorage.setItem(storageKey, String(endTime));
    setSecondsLeft(durationSeconds);
  };

  // Not yet hydrated — render nothing to avoid SSR flash
  if (secondsLeft === null) return null;

  if (secondsLeft <= 0) {
    return (
      <Button
        type="button"
        variant="link"
        size="none"
        onClick={handleResend}
        className={cn("text-[14px] leading-5 underline-offset-2", className)}>
        Resend
      </Button>
    );
  }

  return (
    <span className={cn("text-auth-label text-[14px] leading-5 font-medium", className)}>
      Resend in {secondsLeft}s
    </span>
  );
}

/** localStorage key for resident signup OTP resend cooldown (scoped by email). */
export function signupOtpResendStorageKey(email: string): string {
  const normalized = email.trim().toLowerCase();
  return `pulseprep:signup-otp-resend:${normalized || "unknown"}`;
}

/** Clears persisted end time so the next visit starts a full countdown (e.g. after a new OTP send). */
export function clearSignupOtpResendPersistedEndTime(email: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(signupOtpResendStorageKey(email));
  } catch {
    /* ignore private mode / quota */
  }
}
